/**
 * Collection of tools related to a Category
 *
 * - Title
 * - FeaturedImageUrl
 * - Link
 * - IsActive
 * - IsNotActive
 * - Category
 *
 * @module
 */
import * as React from 'react';
import RouterLink from '../componentsInternal/RouterLink';
import { CategoryContext } from '../contexts';
import { ItemChildren } from '../componentsInternal/utils';
import { useCategory, useIsCategoryActive } from '../hooks';
import { TotalCount as ArticleCount } from './articles';
import { getCategoryPath } from '../utils/routes';
export { ArticleCount };
/**
 * Render the title of a category.
 *
 * - If you don't pass in a `categoryId` then it will use the current category.
 * - Will fetch the category if it's not already in context.
 */
export const Title = ({ categoryId }) => {
    const category = useCategory({ categoryId });
    return React.createElement(React.Fragment, null, category && category.title);
};
Title.displayName = 'Category.Title';
/**
 * Render the featured Image Url of a category.
 *
 * - If you don't pass in a `categoryId` then it will use the current category.
 * - Will fetch the category if it's not already in context.
 */
export const FeaturedImageUrl = ({ className, categoryId, }) => {
    const category = useCategory({ categoryId });
    return !category ? null : (React.createElement("img", { src: category.featuredImageUrl || '', className: className }));
};
FeaturedImageUrl.displayName = 'Category.FeaturedImageUrl';
/**
 * Will render an `<a/>` tag with a `href` that points to a category page.
 *
 * - If you pass in a `categoryId` then it will use that.
 * - Otherwise it will use the current category context.
 *
 * Example usage:
 *
 * ```javascript
 * import * as Category from '@elevio/kb-kit/lib/components/category';
 *
 * export const CategoryLinksWithStyling = () => {
 *   // These styles will apply if the link matches the current category:
 *   const activeStyle: React.CSSProperties = {
 *     color: 'red',
 *   };
 *
 *   return (
 *     <div>
 *       <Category.Link categoryId="123" activeStyle={activeStyle}>
 *         FAQ
 *       </Category.Link>
 *       <Category.Link categoryId="456" activeStyle={activeStyle}>
 *         Services
 *       </Category.Link>
 *     </div>
 *   );
 * };
 * ```
 */
export const Link = ({ children, categoryId, slug, ...props }) => {
    const category = useCategory({ categoryId });
    const isActive = useIsCategoryActive((category === null || category === void 0 ? void 0 : category.id) || categoryId);
    let path = null;
    if (category) {
        path = category.path;
    }
    else if (slug) {
        path = getCategoryPath({ slug });
    }
    else if (categoryId) {
        path = getCategoryPath({ slug: categoryId });
    }
    if (!path)
        return null;
    return (React.createElement(RouterLink, { to: path, isActive: () => isActive, ...props }, children));
};
Link.displayName = 'Category.Link';
/**
 * Renders out the child content if the category either from the `categoryId` prop.
 *
 * - If `categoryId` not provided the current category is active.
 * - If a render prop is passed as the child, then a `boolean` will be provided with the is-active state.
 *
 * > **💡 NOTE**: see [[useIsCategoryActive]] for a definition of what is an active category.
 */
export const IsActive = ({ children, categoryId }) => {
    const isActive = useIsCategoryActive(categoryId);
    if (typeof children === 'function')
        return React.createElement(React.Fragment, null, children(isActive));
    if (!isActive)
        return null;
    return React.createElement(React.Fragment, null, children);
};
IsActive.displayName = 'Category.IsActive';
/**
 * Renders out the child content if the category either from the `categoryId` prop
 *
 * - If `categoryId` not provided, the current category is NOT active.
 * - If a render prop is passed as the child, then a `boolean` will be provided with the is-non-active state.
 *
 * > **💡 NOTE**: see [[useIsCategoryActive]] for a definition of what is an active category.
 */
export const IsNotActive = ({ children, categoryId }) => {
    const isActive = useIsCategoryActive(categoryId);
    if (typeof children === 'function') {
        return React.createElement(React.Fragment, null, children(!isActive));
    }
    if (isActive)
        return null;
    return React.createElement(React.Fragment, null, children);
};
IsNotActive.displayName = 'Category.IsNotActive';
/**
 * Utilises [[useCategory]] under the hood to fetch categories.
 */
export const Render = ({ children, categoryId, usePageCategory, }) => {
    const category = useCategory({ categoryId, usePageCategory });
    if (!category)
        return null;
    return (React.createElement(ItemChildren, { data: category, Provider: CategoryContext.Provider }, children));
};
Render.displayName = 'Category.Render';
//# sourceMappingURL=category.js.map